// ============================================================================
// CrewAhead Maritime Platform - Centralized API Configuration
// ============================================================================
// Single source of truth for all API configuration.
// Used by both the axios client (client.ts) and direct fetch calls.

import { API_ENDPOINTS, STORAGE_KEYS } from './utils/constants';

// ─── Base Configuration ──────────────────────────────────────────────────────

/**
 * Internal backend origin for server-side use (SSR, route handlers).
 * In container deployments this is the container-to-container URL
 * (e.g. http://backend:8000), which differs from the public browser-facing
 * URL. Read at runtime via process.env — not baked at build time.
 */
const SERVER_INTERNAL_URL: string | undefined =
  typeof window === 'undefined'
    ? process.env.BACKEND_INTERNAL_URL?.replace(/\/api\/v1\/?$/, '').replace(/\/$/, '')
    : undefined;

/** Base URL for the API server (without trailing slash and without /api/v1) */
export const API_BASE_URL: string =
  SERVER_INTERNAL_URL ??
  process.env.NEXT_PUBLIC_API_URL
    ?.replace(/\/api\/v1\/?$/, '')
    .replace(/\/$/, '') ??
  'http://127.0.0.1:8000';

/** API version path segment */
export const API_VERSION = 'v1';

/** Full API base path (e.g., /api/v1) */
export const API_BASE_PATH = `/api/${API_VERSION}`;

// ─── Browser vs Server URL Resolution ────────────────────────────────────────

/**
 * Returns the appropriate base URL depending on the environment.
 * - Browser: relative `/api/v1` → routes through Next.js proxy rewrite (avoids CORS)
 * - Server (SSR): full URL → direct backend connection
 */
export function getBaseUrl(): string {
  if (typeof window === 'undefined') {
    // Server-side: use full backend URL
    return `${API_BASE_URL}${API_BASE_PATH}`;
  }
  // Browser: use relative path → Next.js proxy → Django
  return API_BASE_PATH;
}

// ─── Full URL Builder ────────────────────────────────────────────────────────

/**
 * Builds a complete absolute URL from an endpoint path.
 * For use with native `fetch()` which doesn't have a baseURL concept.
 *
 * @param endpoint - Relative endpoint path (e.g., `/notifications`)
 * @param params   - Optional path parameters to interpolate (e.g., `{ id: '123' }`)
 * @returns Complete absolute URL
 *
 * @example
 * ```ts
 * const url = getFullUrl('/notifications');
 * // → 'http://127.0.0.1:8000/api/v1/notifications'
 *
 * const url = getFullUrl('/jobs/:id', { id: '123' });
 * // → 'http://127.0.0.1:8000/api/v1/jobs/123'
 * ```
 */
export function getFullUrl(
  endpoint: string,
  params?: Record<string, string>
): string {
  let path = endpoint;
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      path = path.replace(`:${key}`, encodeURIComponent(value));
    });
  }
  // Ensure path starts with /
  if (!path.startsWith('/')) {
    path = `/${path}`;
  }
  return `${API_BASE_URL}${API_BASE_PATH}${path}`;
}

/**
 * Builds a relative URL for use with axios (which has its own baseURL).
 *
 * @param endpoint - Relative endpoint path
 * @param params   - Optional path parameters to interpolate
 * @returns Relative path ready for axios
 *
 * @example
 * ```ts
 * const path = getApiPath('/notifications');
 * // → '/notifications' (axios prepends baseURL → '/api/v1/notifications')
 * ```
 */
export function getApiPath(
  endpoint: string,
  params?: Record<string, string>
): string {
  let path = endpoint;
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      path = path.replace(`:${key}`, encodeURIComponent(value));
    });
  }
  // Ensure path starts with /
  if (!path.startsWith('/')) {
    path = `/${path}`;
  }
  return path;
}

// ─── Auth Header Utilities ───────────────────────────────────────────────────

/**
 * Returns the Authorization header value for the current access token.
 * Safe for both browser and server environments.
 */
export function getAuthHeader(): { Authorization: string } | Record<string, never> {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
  } catch {
    // localStorage not available
  }
  return {};
}

/**
 * Returns standard headers for API requests including auth and content type.
 * For use with native `fetch()` calls.
 */
export function getApiHeaders(
  contentType: string = 'application/json'
): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': contentType,
    Accept: 'application/json',
  };

  const auth = getAuthHeader();
  if ('Authorization' in auth) {
    headers.Authorization = auth.Authorization;
  }

  return headers;
}

// ─── Endpoint Convenience Exports ────────────────────────────────────────────

/**
 * Pre-built full URLs for commonly used endpoints.
 * Import these when you need a complete URL for `fetch()`.
 */
export const API_URLS = {
  /** Auth endpoints */
  AUTH: {
    LOGIN: () => getFullUrl('/auth/login'),
    REGISTER: () => getFullUrl('/auth/register'),
    LOGOUT: () => getFullUrl('/auth/logout'),
    REFRESH: () => getFullUrl('/auth/token/refresh'),
    VERIFY_EMAIL: () => getFullUrl('/auth/verify-email'),
    ME: () => getFullUrl('/auth/me'),
  },
  /** Notification endpoints */
  NOTIFICATIONS: {
    LIST: () => getFullUrl('/notifications'),
    MARK_READ: (id: string) => getFullUrl('/notifications/:id/mark_read', { id }),
    MARK_ALL_READ: () => getFullUrl('/notifications/mark_all_read'),
  },
  /** Profile endpoints */
  PROFILE: {
    ME: () => getFullUrl('/profile/me'),
    UPDATE: () => getFullUrl('/profile'),
    DOCUMENTS: () => getFullUrl('/profile/documents'),
    SEA_SERVICE: () => getFullUrl('/profile/sea-service'),
    CERTIFICATIONS: () => getFullUrl('/profile/certifications'),
  },
  /** Job endpoints — sourced from API_ENDPOINTS so the URL convention cannot drift. */
  JOBS: {
    LIST: () => getFullUrl(API_ENDPOINTS.JOBS.LIST),
    DETAIL: (id: string) => getFullUrl(API_ENDPOINTS.JOBS.DETAIL(id)),
    APPLY: (id: string) => getFullUrl(API_ENDPOINTS.JOBS.APPLY(id)),
    APPLICATIONS: () => getFullUrl(API_ENDPOINTS.JOBS.APPLICATIONS),
    SAVED: () => getFullUrl(API_ENDPOINTS.JOBS.SAVED),
  },
  /** Availability endpoints — sourced from API_ENDPOINTS so the URL convention cannot drift. */
  AVAILABILITY: {
    LIST: () => getFullUrl(API_ENDPOINTS.AVAILABILITY.LIST),
    MY_POSTS: () => getFullUrl(API_ENDPOINTS.AVAILABILITY.MY_POSTS),
  },
} as const;

// ─── Legacy Compatibility ────────────────────────────────────────────────────

/**
 * @deprecated Use `API_BASE_URL` directly. Kept for backward compatibility.
 */
export const API_BASE = API_BASE_URL;

/**
 * @deprecated Use `getFullUrl()` directly. Kept for backward compatibility.
 */
export const getEndpoint = getFullUrl;
